// const companyId = String(localStorage.getItem("companyId"));
// helpers
import { useAccessPermission } from "@/helpers/acessPermission";

// Third Party
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";

// UI
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import TableSkeleton from "@/components/skeleton/TableSkeleton";

// Icons
import { PlusCircle, Eye } from "lucide-react";

// Services and Types
import { getSubSOEs } from "@/services/soe";
import type { SOE } from "@/types/soe";
import { S3_BUCKET_LINK } from "@/components/constants";
import formatDate from "@/utils/formatDate";
import { clearLocalStorage } from "@/helpers/accessLocalStorage";

export const Route = createFileRoute(
  "/_layout-private/account-management/soe/_information/joint-venture/"
)({
  component: JointVentures,
});

function JointVentures() {
  const navigate = useNavigate();
  const companyId = String(localStorage.getItem("companyId")).replace(/"/g, "");
  const { company_management } = useAccessPermission();

  // Query
  const {
    data: soes,
    isLoading,
    error: SOEDataError,
  } = useQuery({
    queryKey: ["joint-venture", companyId],
    queryFn: () =>
      getSubSOEs({
        companyId: companyId,
        limit: 100,
        skip: 0,
      }),
  });

  if (SOEDataError) {
    const isAxiosError = (
      error: unknown
    ): error is { response?: { data?: { message?: string } } } => {
      return typeof error === "object" && error !== null && "response" in error;
    };

    if (
      isAxiosError(SOEDataError) &&
      SOEDataError?.response?.data?.message === "LOGGED_OUT"
    ) {
      console.log("clearing storage due to logged out");
      clearLocalStorage();
      window.location.reload();
    }
  }

  if (isLoading) {
    return <TableSkeleton />;
  }

  const data = soes?.data as SOE[];

  return (
    <div className="space-y-[24px] ">
      <div className="text-[18px]">
        <h2 className="font-semibold text-zinc-950">
          ຂໍ້ມູນບໍລິສັດຍ່ອຍ / ໃນເຄືອ
        </h2>
        <p className="text-zinc-500">
          ຕາຕະລາງຂໍ້ມູນບໍລິສັດຍ່ອຍ / ໃນເຄືອຂອງວິສາຫະກິດ
        </p>

        <Separator className="my-6" />
      </div>

      <div className="flex justify-end w-full">
        {company_management.isWrite && (
          <Button
            onClick={() =>
              navigate({
                to: "/account-management/soe/joint-venture/mutate",
                search: {
                  childId: undefined,
                  data: undefined,
                },
              })
            }
          >
            <PlusCircle className="mr-2" />
            ເພີ່ມຂໍ້ມູນ
          </Button>
        )}
      </div>

      {/* FIXME: Replace with Data Table Component */}

      <Table className="border rounded-md">
        <TableHeader className="">
          <TableRow>
            {tableData.headers.map((header) => (
              <TableHead className="whitespace-nowrap" key={header}>
                {header}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {data?.map((item, index) => (
            <TableRow
              key={item?.id}
              className="cursor-pointer"
              onClick={() =>
                navigate({
                  to: "/account-management/soe/joint-venture/$id",
                  params: { id: item.id },
                })
              }
            >
              <TableCell className="whitespace-nowrap">{index + 1}</TableCell>
              <TableCell className="whitespace-nowrap">
                <div className="flex items-center gap-2">
                  <Eye className="text-blue-500" />
                  <Avatar>
                    <AvatarImage
                      className="object-cover"
                      src={S3_BUCKET_LINK + item?.companyProfileUrl}
                    />
                    <AvatarFallback>
                      {item?.companyName?.charAt(0)}
                    </AvatarFallback>
                  </Avatar>
                </div>
              </TableCell>
              <TableCell className="whitespace-nowrap">
                {item?.registrationNo}
              </TableCell>

              <TableCell className="whitespace-nowrap">
                {item?.taxpayerId}
              </TableCell>
              <TableCell className="whitespace-nowrap">
                {item?.companyName}
              </TableCell>
              {/* <TableCell className="whitespace-nowrap">
								{item?.coreSector?.coreSector}
							</TableCell> */}
              <TableCell className="whitespace-nowrap">
                {item?.govShareholding}%
              </TableCell>
              <TableCell className="whitespace-nowrap">
                {formatDate({
                  date: item?.establishedDate,
                  format: "DD/MM/YYYY",
                })}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

const tableData = {
  headers: [
    "ລຳດັບ",
    "ຮູບໂລໂກ້",
    "ເລກທະບຽນວິສາຫະກິດ",
    "ເລກປະຈຳຕົວຜູ້ເສຍອາກອນ",
    "ຊື່ລັດວິສາຫະກິດ",
    // "ຂະແໜງການ",
    "ລັດຖືຫຸ້ນ",
    "ມື້ສ້າງຕັ້ງ",
  ],
};
